import React from "react";
import { ShoppingBag } from "lucide-react";

/**
 * Small branded pill for a merchant name. Uses a deterministic gradient
 * seeded off the merchant name so every store has a consistent, recognisable
 * chip WITHOUT relying on an external logo image (which frequently 404s).
 */

const PALETTES = {
  amazon: { fg: "#111827", bg: "linear-gradient(135deg,#FFF3CE 0%,#FFC46B 100%)", ring: "#F59E0B" },
  flipkart: { fg: "#FFFFFF", bg: "linear-gradient(135deg,#2874F0 0%,#0B49B8 100%)", ring: "#2874F0" },
  myntra: { fg: "#FFFFFF", bg: "linear-gradient(135deg,#FF3F6C 0%,#B4224B 100%)", ring: "#FF3F6C" },
  nykaa: { fg: "#FFFFFF", bg: "linear-gradient(135deg,#FC2779 0%,#88134C 100%)", ring: "#FC2779" },
  ajio: { fg: "#FFFFFF", bg: "linear-gradient(135deg,#2C2C2C 0%,#0F0F0F 100%)", ring: "#2C2C2C" },
  meesho: { fg: "#FFFFFF", bg: "linear-gradient(135deg,#F43397 0%,#A82278 100%)", ring: "#F43397" },
  bigbasket: { fg: "#FFFFFF", bg: "linear-gradient(135deg,#84C225 0%,#4D7215 100%)", ring: "#84C225" },
  zomato: { fg: "#FFFFFF", bg: "linear-gradient(135deg,#E23744 0%,#8B1A24 100%)", ring: "#E23744" },
  swiggy: { fg: "#111827", bg: "linear-gradient(135deg,#FFDE9D 0%,#FC8019 100%)", ring: "#FC8019" },
  tata: { fg: "#FFFFFF", bg: "linear-gradient(135deg,#486AAB 0%,#243A66 100%)", ring: "#486AAB" },
  default: { fg: "#065F46", bg: "linear-gradient(135deg,#D1FAE5 0%,#6EE7B7 100%)", ring: "#10B981" },
};

function paletteFor(name) {
  if (!name) return PALETTES.default;
  const key = name.toLowerCase().trim();
  for (const k of Object.keys(PALETTES)) {
    if (key.includes(k)) return PALETTES[k];
  }
  return PALETTES.default;
}

function initialsOf(name) {
  if (!name) return "M";
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
}

const SIZES = {
  sm: { pill: "text-[10px] px-2 py-1", dot: "h-4 w-4 text-[9px]", iconGap: "gap-1.5" },
  md: { pill: "text-xs px-2.5 py-1", dot: "h-5 w-5 text-[10px]", iconGap: "gap-1.5" },
  lg: { pill: "text-sm px-3 py-1.5", dot: "h-6 w-6 text-[11px]", iconGap: "gap-2" },
};

const MerchantChip = ({ name, size = "md", showIcon = false, className = "" }) => {
  const p = paletteFor(name);
  const s = SIZES[size] || SIZES.md;
  return (
    <span
      className={`inline-flex items-center ${s.iconGap} rounded-full font-semibold ${s.pill} ${className}`}
      style={{ background: p.bg, color: p.fg, boxShadow: `0 0 0 1px ${p.ring}22, 0 1px 2px ${p.ring}22` }}
      data-testid="merchant-chip"
      title={name}
    >
      {showIcon ? (
        <ShoppingBag className="h-3 w-3 opacity-80" />
      ) : (
        <span
          className={`grid place-items-center rounded-full font-bold ${s.dot}`}
          style={{ background: "#ffffff33", color: p.fg === "#FFFFFF" ? "#fff" : p.fg }}
        >
          {initialsOf(name)}
        </span>
      )}
      <span className="truncate max-w-[110px]">{name || "Merchant"}</span>
    </span>
  );
};

export default MerchantChip;
